<template>
  <aside
    ref="sidebarRef"
    class="dashboard-sidebar"
    :class="[
      { 'dashboard-sidebar--open': isDrawerOpen },
      { 'dashboard-sidebar--collapsed': isCollapsedState },
      { 'dashboard-sidebar--tablet': isTablet },
    ]"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <!-- Backdrop mobile : ferme le drawer au clic -->
    <div
      v-if="isDrawerOpen"
      class="dashboard-sidebar__backdrop"
      aria-hidden="true"
      @click="closeDrawer"
    />

    <!-- Mobile : overlay avec styles inline pour garantir visibilité (échappe à tout CSS externe) -->
    <div
      v-if="isDrawerOpen"
      class="dashboard-sidebar__mobile-drawer"
      :style="mobileDrawerStyle"
    >
      <div class="dashboard-sidebar__mobile-drawer__logo">
        <router-link :to="props.homeRoute" class="dashboard-sidebar__mobile-drawer__logo-link" @click="closeDrawer">
          <img
            v-show="!logoError"
            :src="logoImg"
            alt="patrimonea by Gwem"
            width="160"
            height="72"
            @error="logoError = true"
          />
          <template v-if="logoError">
            <div class="dashboard-sidebar__mobile-drawer__logo-icon">
              <component :is="ChartPieIcon" class="w-7 h-7 text-primary" />
            </div>
            <span class="dashboard-sidebar__mobile-drawer__logo-text">patrimonea by Gwem</span>
          </template>
        </router-link>
      </div>
      <nav class="dashboard-sidebar__mobile-drawer__nav">
        <router-link
          v-for="item in visibleMenuItems"
          :key="item.to"
          :to="item.to"
          class="dashboard-sidebar__mobile-drawer__link"
          active-class="dashboard-sidebar__mobile-drawer__link--active"
          @click="closeDrawer"
        >
          <span class="dashboard-sidebar__mobile-drawer__link-icon">
            <component :is="item.icon || HomeIcon" class="w-5 h-5" />
          </span>
          <span class="dashboard-sidebar__mobile-drawer__link-label">{{ item.name }}</span>
        </router-link>
      </nav>
    </div>

    <!-- Desktop / tablet -->
    <div
      v-show="!isDrawerOpen"
      class="dashboard-sidebar__inner"
    >
      <div class="dashboard-sidebar__logo">
        <router-link :to="props.homeRoute" class="dashboard-sidebar__logo-link" @click="closeDrawer">
          <img
            v-show="!logoError"
            :src="logoImg"
            alt="patrimonea by Gwem"
            class="dashboard-sidebar__logo-img"
            width="160"
            height="72"
            @error="logoError = true"
          />
          <template v-if="logoError">
            <div class="dashboard-sidebar__logo-icon flex-shrink-0 w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center">
              <component :is="ChartPieIcon" class="w-7 h-7 text-primary" />
            </div>
            <span v-show="showLabels" class="dashboard-sidebar__logo-text font-semibold text-navy truncate">
              Patrimonea <span class="text-primary">by Gwem</span>
            </span>
          </template>
        </router-link>
      </div>

      <button
        v-if="showCollapseToggle"
        type="button"
        class="dashboard-sidebar__toggle"
        :aria-label="isCollapsedState ? 'Agrandir le menu' : 'Réduire le menu'"
        @click="collapsed = !collapsed"
      >
        <component :is="isCollapsedState ? ChevronRightIcon : ChevronLeftIcon" class="w-5 h-5 text-navy" />
      </button>

      <nav class="dashboard-sidebar__nav">
        <div class="dashboard-sidebar__indicator-wrap">
          <div class="dashboard-sidebar__indicator" :style="indicatorStyle" />
        </div>
        <router-link
          v-for="(item, index) in visibleMenuItems"
          :key="item.to"
          :to="item.to"
          class="dashboard-sidebar__link"
          active-class="dashboard-sidebar__link--active"
          @click="closeDrawer"
        >
          <span class="dashboard-sidebar__link-icon">
            <component :is="item.icon || HomeIcon" class="w-5 h-5" />
          </span>
          <span v-show="showLabels" class="dashboard-sidebar__link-label truncate">{{ item.name }}</span>
        </router-link>
      </nav>
    </div>
  </aside>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ChartPieIcon,
  HomeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/vue/24/outline';
import logoImg from '@/assets/logo.png';
import { getProfileSessionItem } from '@/utils/sessionAuth';
import { syncEmployeProfileFromApi } from '@/utils/permissions';
import { getVerifiedPermissions, getVerifiedRole, getVerifiedRoleEmploye } from '@/utils/verifiedProfile';
import { filterSidebarItemsByProfile, MODULE1_SIDEBAR_MENU_ITEMS } from './sidebarProfileMenus';

// --- Props / Emits (drawer mobile contrôlé uniquement par la prop) ---
const props = defineProps({
  collapsed: { type: Boolean, default: null },
  isTablet: { type: Boolean, default: false },
  /** État d'ouverture du drawer mobile (contrôlé par le layout) */
  mobileDrawerOpen: { type: Boolean, default: false },
  menuItems: { type: Array, default: () => [] },
  homeRoute: { type: String, default: '/analyse-patrimoniale/dashboards' },
});
const emit = defineEmits(['update:collapsed', 'close-drawer']);
const router = useRouter();

// --- État local ---
const sidebarRef = ref(null);
const logoError = ref(false);
const collapsedLocal = ref(false);
const userRole = ref('');
/** Incrémenté après sync profil pour recalculer le menu (user_permissions n’est pas réactif). */
const menuRevision = ref(0);
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

// --- Breakpoints (≤776px = drawer full contenu, >776 = tablet/desktop) ---
const MOBILE_MAX_WIDTH = 776;
const isMobile = computed(() => windowWidth.value <= MOBILE_MAX_WIDTH);
const canUseDrawer = computed(() => isMobile.value || props.isTablet);

// --- Drawer mobile : uniquement la prop (pas d'inject) ---
const isDrawerOpen = computed(() => canUseDrawer.value && props.mobileDrawerOpen);

function closeDrawer() {
  emit('close-drawer');
}

// --- Collapsed / labels / toggle ---
const collapsed = computed({
  get: () => (props.collapsed !== null && props.collapsed !== undefined ? props.collapsed : collapsedLocal.value),
  set: (v) => {
    collapsedLocal.value = v;
    emit('update:collapsed', v);
  },
});
const isCollapsedState = computed(() => {
  if (props.isTablet) return !isDrawerOpen.value;
  return collapsed.value;
});
const showLabels = computed(() => {
  if (isMobile.value && isDrawerOpen.value) return true;
  return !isCollapsedState.value;
});
const showCollapseToggle = computed(() => !isMobile.value && !props.isTablet && windowWidth.value >= 1024);

// --- Menu : source unique (sidebarProfileMenus.js) ou override via props.menuItems ---
const resolvedMenuItems = computed(() => (props.menuItems?.length ? props.menuItems : MODULE1_SIDEBAR_MENU_ITEMS));

function readPermissionsFromSession() {
  const verified = getVerifiedPermissions();
  if (verified.length) return verified;
  try {
    const raw = getProfileSessionItem('user_permissions');
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch (_) {
    return [];
  }
}

const visibleMenuItems = computed(() => {
  void menuRevision.value;
  const role = (
    userRole.value ||
    getVerifiedRole() ||
    getProfileSessionItem('user_role') ||
    ''
  )
    .toString()
    .trim()
    .toLowerCase();
  return filterSidebarItemsByProfile(
    role,
    readPermissionsFromSession(),
    resolvedMenuItems.value,
    getVerifiedRoleEmploye()
  );
});

// --- Indicateur de route active ---
const route = useRoute();
const activeIndex = computed(() => {
  const path = route.path.replace(/\/$/, '') || route.path;
  const idx = visibleMenuItems.value.findIndex((item) => {
    const to = (item.to || '').replace(/\/$/, '') || item.to;
    return path === to || path.startsWith(to + '/');
  });
  return idx >= 0 ? idx : 0;
});
const LINK_HEIGHT = 44;
const LINK_GAP = 2;
const indicatorStyle = computed(() => ({
  transform: `translateY(${activeIndex.value * (LINK_HEIGHT + LINK_GAP)}px)`,
}));

// Styles inline pour l’overlay mobile (priorité max, aucun CSS ne peut les écraser)
const mobileDrawerStyle = computed(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem',
  overflow: 'auto',
  overflowX: 'hidden',
  background: '#d89c24',
  visibility: 'visible',
  opacity: 1,
  minHeight: '100vh',
}));

// --- Touch : fermer le drawer par swipe vers la gauche ---
let touchStartX = 0;
let touchCurrentX = 0;
function onTouchStart(e) {
  touchStartX = e.touches[0].clientX;
  touchCurrentX = touchStartX;
}
function onTouchMove(e) {
  touchCurrentX = e.touches[0].clientX;
}
function onTouchEnd() {
  const delta = touchCurrentX - touchStartX;
  if (!isMobile.value) return;
  if (isDrawerOpen.value && delta < -60) emit('close-drawer');
}

// --- Resize + chargement du rôle ---
function checkWidth() {
  windowWidth.value = window.innerWidth;
}

function syncUserRole() {
  const role =
    getVerifiedRole() ||
    getProfileSessionItem('user_role') ||
    '';
  userRole.value = role ? String(role).trim().toLowerCase() : '';
}

watch(
  () => isDrawerOpen.value,
  (open) => {
    if (open) syncUserRole();
  }
);

watch(
  () => route.path,
  () => {
    syncUserRole();
  }
);

onMounted(async () => {
  checkWidth();
  await syncEmployeProfileFromApi();
  syncUserRole();
  menuRevision.value += 1;
  window.addEventListener('resize', checkWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkWidth);
});
</script>

<style scoped>
.dashboard-sidebar {
  @apply fixed left-0 top-0 z-[1000] h-screen border-r flex flex-col;
  width: 260px;
  font-family: var(--font-body);
  /* Or sidebar charte — teinte écran staff (légèrement plus chaude que --brand-gold) */
  background: #d89c24;
  border-color: color-mix(in srgb, #d89c24 20%, transparent);
  box-shadow: 4px 0 24px -4px rgba(15, 23, 42, 0.08);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.dashboard-sidebar--collapsed {
  width: 72px;
}

.dashboard-sidebar__backdrop {
  @apply fixed inset-0 bg-navy/20 backdrop-blur-sm;
  z-index: 0;
  transition: opacity 0.3s ease;
}

/* Overlay mobile : contenu garanti visible (conteneur en inline style) */
.dashboard-sidebar__mobile-drawer__logo {
  flex-shrink: 0;
  margin-bottom: 1rem;
}
.dashboard-sidebar__mobile-drawer__logo-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #fff;
  text-decoration: none;
}
.dashboard-sidebar__mobile-drawer__logo-link img {
  max-width: 160px;
  height: auto;
  max-height: 72px;
  object-fit: contain;
}
.dashboard-sidebar__mobile-drawer__logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.8);
}
.dashboard-sidebar__mobile-drawer__logo-text {
  color: #fff;
  font-weight: 600;
  font-size: 0.875rem;
}
.dashboard-sidebar__mobile-drawer__nav {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-height: 0;
}
.dashboard-sidebar__mobile-drawer__link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.5rem;
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  min-height: 44px;
  border-radius: 0.75rem;
  transition: background 0.2s;
}
.dashboard-sidebar__mobile-drawer__link:hover {
  background: rgba(255, 255, 255, 0.2);
}
.dashboard-sidebar__mobile-drawer__link--active {
  background: rgba(255, 255, 255, 0.25);
}
.dashboard-sidebar__mobile-drawer__link-icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  color: rgba(255, 255, 255, 0.95);
}
.dashboard-sidebar__mobile-drawer__link-label {
  color: #fff;
}

.dashboard-sidebar__inner {
  @apply relative flex flex-col h-full overflow-hidden py-4 sm:py-5;
  z-index: 1;
}

@media (max-width: 768px) {
  .sidebar, [class*="sidebar"] {
    transition: transform .25s ease;
  } 
}
.dashboard-sidebar__logo {
  @apply flex justify-center mb-4 sm:mb-6 flex-shrink-0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.dashboard-sidebar__logo-link {
  @apply flex items-center justify-center gap-3 overflow-hidden w-full;
}

.dashboard-sidebar__logo-img {
  height: 110px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  flex-shrink: 0;
}

.dashboard-sidebar--collapsed .dashboard-sidebar__logo-img {
  max-width: 48px;
  height: 40px;
}

.dashboard-sidebar__logo-text {
  @apply text-slate-800 text-sm;
}

.dashboard-sidebar__toggle {
  @apply absolute -right-3 top-20 w-6 h-6 rounded-full shadow-soft flex items-center justify-center transition-colors z-10;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(212, 160, 23, 0.3);
}
.dashboard-sidebar__toggle:hover {
  background: #fff;
}

.dashboard-sidebar__nav {
  @apply relative flex flex-col flex-1 overflow-y-auto px-2 sm:px-3;
  gap: 0;
}

.dashboard-sidebar__link {
  @apply relative flex items-center gap-3 px-2 sm:px-3 rounded-xl font-medium text-sm transition-colors duration-200 flex-shrink-0;
  height: 44px;
  min-height: 44px;
  margin-bottom: 2px;
  /* Navy sur fond doré : 6,4:1. Le slate précédent plafonnait à 4,28:1. */
  color: var(--brand-navy);
  box-sizing: border-box;
}

.dashboard-sidebar__link:last-child {
  margin-bottom: 0;
}

.dashboard-sidebar__indicator-wrap {
  @apply absolute left-2 sm:left-3 right-2 sm:right-3 pointer-events-none;
  top: 0;
  height: 44px;
}

.dashboard-sidebar__indicator {
  @apply absolute left-0 top-0 w-full rounded-xl;
  height: 44px;
  background: rgba(255, 251, 235, 0.92);
  box-shadow: 0 1px 3px rgba(212, 160, 23, 0.15);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dashboard-sidebar__link:hover {
  background: rgba(255, 255, 255, 0.4);
  color: var(--brand-ink);
}

.dashboard-sidebar__link--active {
  color: var(--brand-brown);
}

.dashboard-sidebar__link--active .dashboard-sidebar__link-icon {
  background: var(--paper);
  color: var(--brand-gold-dark);
  box-shadow: 0 0 0 1px rgba(212, 160, 23, 0.2), 0 2px 8px rgba(212, 160, 23, 0.18);
}

.dashboard-sidebar__link-icon {
  @apply flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors duration-200;
}

/* ========== Mobile (≤776px) : drawer plein (icônes + libellés) ========== */
@media (max-width: 776px) {
  .dashboard-sidebar {
    width: 260px;
    min-height: 100vh;
    height: 100vh;
    transform: translateX(-100%);
  }
  .dashboard-sidebar--open {
    transform: translateX(0);
    height: 100vh;
    z-index: 9999;
  }
  /* Contenu visible : ciblage par classe + --open pour forcer l’affichage */
  .dashboard-sidebar__inner--mobile-open,
  .dashboard-sidebar--open .dashboard-sidebar__inner {
    display: flex !important;
    flex-direction: column;
    min-height: 100vh;
    height: 100%;
    overflow: visible !important;
    visibility: visible !important;
    opacity: 1 !important;
    position: relative;
    z-index: 2;
    padding: 1rem;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__logo,
  .dashboard-sidebar--open .dashboard-sidebar__logo {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
    flex-shrink: 0;
    margin-bottom: 1rem;
    min-height: 64px;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__logo-link,
  .dashboard-sidebar--open .dashboard-sidebar__logo-link {
    display: flex !important;
    align-items: center;
    justify-content: center;
    overflow: visible !important;
    visibility: visible !important;
    min-height: 64px;
    color: #fff !important;
    text-decoration: none !important;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__logo-img,
  .dashboard-sidebar--open .dashboard-sidebar__logo-img {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    max-width: 160px;
    width: auto;
    min-width: 80px;
    height: auto;
    min-height: 48px;
    max-height: 72px;
    object-fit: contain;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__logo-icon,
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__logo-text,
  .dashboard-sidebar--open .dashboard-sidebar__logo-icon,
  .dashboard-sidebar--open .dashboard-sidebar__logo-text {
    color: #fff;
    visibility: visible !important;
    opacity: 1 !important;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__nav,
  .dashboard-sidebar--open .dashboard-sidebar__nav {
    display: flex !important;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
    visibility: visible !important;
    opacity: 1 !important;
    gap: 2px;
    min-height: 0;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__link,
  .dashboard-sidebar--open .dashboard-sidebar__link {
    display: flex !important;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.5rem;
    color: #fff;
    min-height: 44px;
    visibility: visible !important;
    opacity: 1 !important;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__link-label,
  .dashboard-sidebar--open .dashboard-sidebar__link-label {
    display: inline !important;
    visibility: visible !important;
    color: #fff;
  }
  .dashboard-sidebar__inner--mobile-open .dashboard-sidebar__link-icon,
  .dashboard-sidebar--open .dashboard-sidebar__link-icon {
    color: rgba(255, 255, 255, 0.95);
    flex-shrink: 0;
    visibility: visible !important;
  }
  .dashboard-sidebar--open .dashboard-sidebar__link:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }
  .dashboard-sidebar--open .dashboard-sidebar__link--active {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
  }
  .dashboard-sidebar--open .dashboard-sidebar__link--active .dashboard-sidebar__link-icon {
    color: #fff;
  }
  /* Forcer les <a> (router-link) à être visibles */
  .dashboard-sidebar__inner--mobile-open a,
  .dashboard-sidebar--open .dashboard-sidebar__inner a {
    color: #fff !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  .dashboard-sidebar--collapsed {
    width: 260px;
  }
}

/* ========== Tablette (777–1023px) : barre icônes ========== */
@media (min-width: 777px) and (max-width: 1023px) {
  .dashboard-sidebar {
    width: 72px;
    transform: translateX(0);
  }
  .dashboard-sidebar--open {
    width: 260px;
    z-index: 9999;
    box-shadow: 8px 0 24px rgba(15, 23, 42, 0.16);
  }
  .dashboard-sidebar__logo .dashboard-sidebar__logo-text {
    @apply sr-only;
  }
  .dashboard-sidebar--open .dashboard-sidebar__backdrop {
    display: block;
  }
}

/* ========== Desktop (≥ 1024px) ========== */
@media (min-width: 1024px) {
  .dashboard-sidebar {
    transform: translateX(0);
  }
}
</style>
